import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { COMPANY } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = ["FormFlo Demo", "MemberSpace Demo", "Partnership", "General Inquiry"];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    interest: "General Inquiry",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "", interest: "General Inquiry" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 sm:py-32 border-t border-white/5"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4]">
              Get in Touch
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white">
              Start Your Build
            </h2>
            <p className="mt-4 text-base text-[#94A3B8] leading-relaxed max-w-md">
              Tell us about your operational challenge. Our team will show you
              how Rishmathi can engineer an intelligent solution—fast.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-4 group"
                data-testid="contact-email-link"
              >
                <div className="h-11 w-11 rounded-xl bg-[#6366F1]/15 border border-white/10 flex items-center justify-center group-hover:border-[#6366F1]/40 transition-colors">
                  <Mail className="text-[#06B6D4]" size={18} />
                </div>
                <div>
                  <div className="text-xs text-[#64748B]">Email</div>
                  <div className="text-white group-hover:text-[#06B6D4] transition-colors">
                    {COMPANY.email}
                  </div>
                </div>
              </a>
              <a
                href={`tel:${COMPANY.phone}`}
                className="flex items-center gap-4 group"
                data-testid="contact-phone-link"
              >
                <div className="h-11 w-11 rounded-xl bg-[#6366F1]/15 border border-white/10 flex items-center justify-center group-hover:border-[#6366F1]/40 transition-colors">
                  <Phone className="text-[#06B6D4]" size={18} />
                </div>
                <div>
                  <div className="text-xs text-[#64748B]">Phone</div>
                  <div className="text-white group-hover:text-[#06B6D4] transition-colors">
                    +91 {COMPANY.phone}
                  </div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-[#6366F1]/15 border border-white/10 flex items-center justify-center">
                  <MapPin className="text-[#06B6D4]" size={18} />
                </div>
                <div>
                  <div className="text-xs text-[#64748B]">Registered Location</div>
                  <div className="text-white">{COMPANY.location}</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={handleSubmit}
            className="bg-[#0F172A]/60 border border-white/10 rounded-2xl p-8 backdrop-blur-sm"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-[#94A3B8]">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Jane Doe"
                  className="mt-2 bg-[#020617]/60 border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#6366F1] focus-visible:border-[#6366F1]"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-[#94A3B8]">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="jane@company.com"
                  className="mt-2 bg-[#020617]/60 border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#6366F1] focus-visible:border-[#6366F1]"
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
              <div>
                <Label htmlFor="company" className="text-[#94A3B8]">
                  Company
                </Label>
                <Input
                  id="company"
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  placeholder="Acme Inc."
                  className="mt-2 bg-[#020617]/60 border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#6366F1] focus-visible:border-[#6366F1]"
                  data-testid="contact-company-input"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">I'm interested in</Label>
                <Select
                  value={form.interest}
                  onValueChange={(v) => update("interest", v)}
                >
                  <SelectTrigger
                    className="mt-2 bg-[#020617]/60 border-white/10 text-white focus:ring-[#6366F1]"
                    data-testid="contact-interest-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F172A] border-white/10 text-white">
                    {interests.map((i) => (
                      <SelectItem key={i} value={i} className="focus:bg-[#6366F1]/20 focus:text-white">
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="message" className="text-[#94A3B8]">
                Message *
              </Label>
              <Textarea
                id="message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Tell us about your project or challenge..."
                rows={5}
                className="mt-2 bg-[#020617]/60 border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#6366F1] focus-visible:border-[#6366F1] resize-none"
                data-testid="contact-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#6366F1] text-white hover:bg-[#4F46E5] transition-all rounded-full px-8 py-4 font-medium shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Sending...
                </>
              ) : (
                <>
                  Send Message <Send size={16} />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
